import { FC, useMemo } from 'react';
import { css } from '@emotion/react';
import { ListingStatus } from '@weave/schema-gen-ts/dist/schemas/forms-digital/weave_digital_forms.pb';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Heading, Tabs, Text } from '@frontend/design-system';
import { useGetKioskForms, useGetKioskPackets } from '../../../../hooks/api-hooks';
import { KioskFormOrPacketList, KioskListItem } from './kiosk-forms-or-packets-list';

interface Props {
  locationId: string;
  onSelect: (idKey: 'formId' | 'packetId', idValue: string) => void;
}
export const KioskFormsListing: FC<Props> = ({ locationId, onSelect }) => {
  const { t } = useTranslation('digital-forms-experience');
  const { forms, isLoading: isLoadingKioskForms } = useGetKioskForms(locationId);
  const { packets, isLoading: isLoadingKioskPackets } = useGetKioskPackets(locationId);

  const hasPackets = packets.length > 0;

  const handleSelect = (idKey: 'formId' | 'packetId', idValue: string | string[]) => {
    if (Array.isArray(idValue)) {
      // Handle multiple selection case if needed
      console.error('Multiple selection is not supported for forms or packets');
      return;
    }

    onSelect(idKey, idValue);
  };

  const formItems = useMemo(
    () =>
      forms.reduce<KioskListItem[]>((acc, form) => {
        if (!form.id || !form.name) return acc;

        if (!acc.some((existing) => existing.value === form.id)) {
          acc.push({
            label: form.name ?? '',
            value: form.id ?? '',
            shouldShowWeaveLogo: !!form.formPmsId,
            isPinned: form.listingStatus === ListingStatus.PINNED,
          });
        }

        return acc;
      }, []),
    [forms]
  );

  const packetItems = useMemo(
    () =>
      packets.reduce<KioskListItem[]>((acc, packet) => {
        if (!packet.id || !packet.name) return acc;

        if (!acc.some((existing) => existing.value === packet.id)) {
          acc.push({
            label: packet.name ?? '',
            value: packet.id ?? '',
            formsCount: packet.formCount ?? 0,
            isPinned: packet.listingStatus === ListingStatus.PINNED,
          });
        }

        return acc;
      }, []),
    [packets]
  );

  return (
    <section css={containerStyle}>
      <Heading level={3}>{t('Select Form to fill')}</Heading>
      <Text color='light'>{t('Choose the forms below to begin entering information and submit')}</Text>
      <Tabs initialTab='forms'>
        <Tabs.Bar>
          {hasPackets && <Tabs.Tab id='packets'>{t('Packets')}</Tabs.Tab>}
          <Tabs.Tab id='forms'>{t('Forms')}</Tabs.Tab>
        </Tabs.Bar>
        {hasPackets && (
          <Tabs.Panel controller='packets'>
            <KioskFormOrPacketList
              type='packets'
              list={packetItems}
              isLoading={isLoadingKioskPackets}
              onSelect={(idValue) => handleSelect('packetId', idValue)}
            />
          </Tabs.Panel>
        )}
        <Tabs.Panel controller='forms'>
          <KioskFormOrPacketList
            type='forms'
            list={formItems}
            isLoading={isLoadingKioskForms}
            onSelect={(idValue) => handleSelect('formId', idValue)}
          />
        </Tabs.Panel>
      </Tabs>
    </section>
  );
};

export const containerStyle = css({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
});
